import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Mail } from 'lucide-react';
import { COLORS } from '@/constants/calculator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PURPLE_BG_STYLE = { backgroundColor: COLORS.PURPLE };
const ORANGE_BG_STYLE = { backgroundColor: COLORS.ORANGE };
const PURPLE_TEXT_STYLE = { color: COLORS.PURPLE };

const handleTriggerMouseEnter = (e) => {
  e.currentTarget.style.backgroundColor = COLORS.PURPLE_DARK;
};
const handleTriggerMouseLeave = (e) => {
  e.currentTarget.style.backgroundColor = COLORS.PURPLE;
};

export const LeadFormDialog = ({ values, calculated }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error('Please enter your name and email');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name,
        email,
        company,
        phone,
        managed_cost: values.managedCost,
        self_delivery_cost: calculated.selfDeliveryCostInclusive,
        holiday_pay: values.holidayPay,
        supervisor_cost: values.supervisorCost,
        non_productive_time: values.nonProductiveTime,
        non_essential_downtime: values.nonEssentialDowntime,
      });
      toast.success('Thanks! ARLO will be in touch shortly.');
      setOpen(false);
      setName('');
      setEmail('');
      setCompany('');
      setPhone('');
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409) {
        toast.error(detail || 'This email has already been submitted recently.');
      } else if (status === 429) {
        toast.error(detail || 'Too many submissions. Please try again later.');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          className="inline-flex items-center justify-center gap-2 font-bold text-base px-6 py-3 text-white transition-all duration-200"
          style={PURPLE_BG_STYLE}
          onMouseEnter={handleTriggerMouseEnter}
          onMouseLeave={handleTriggerMouseLeave}
          data-testid="email-results-btn"
        >
          <Mail className="h-5 w-5" />
          EMAIL ME THESE RESULTS
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="lead-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black" style={PURPLE_TEXT_STYLE}>
            Get Your Results
          </DialogTitle>
          <DialogDescription>
            Enter your details and we&apos;ll send you a copy of your cost comparison and follow up with a tailored quote.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-4">
          <div>
            <Label htmlFor="lead-name" className="text-sm font-semibold">Name *</Label>
            <Input
              id="lead-name"
              data-testid="lead-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your full name"
              className="mt-1"
              required
            />
          </div>
          <div>
            <Label htmlFor="lead-email" className="text-sm font-semibold">Email *</Label>
            <Input
              id="lead-email"
              data-testid="lead-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="mt-1"
              required
            />
          </div>
          <div>
            <Label htmlFor="lead-company" className="text-sm font-semibold">Company</Label>
            <Input
              id="lead-company"
              data-testid="lead-company-input"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Company name"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="lead-phone" className="text-sm font-semibold">Phone</Label>
            <Input
              id="lead-phone"
              data-testid="lead-phone-input"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+64 ..."
              className="mt-1"
            />
          </div>
          <div className="bg-slate-50 border border-slate-200 p-4 text-sm">
            <p className="font-semibold mb-2 text-slate-900">Your calculation snapshot:</p>
            <div className="grid grid-cols-2 gap-2 text-slate-600">
              <span>Managed Cost:</span>
              <span className="font-bold text-slate-900">${values.managedCost}/hr</span>
              <span>Self Delivery (cost-inclusive):</span>
              <span className="font-bold text-slate-900">${calculated.selfDeliveryCostInclusive.toFixed(2)}/hr</span>
              <span>Total Management Cost:</span>
              <span className="font-bold text-slate-900">${calculated.totalCosts.toFixed(2)}/hr</span>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full font-bold text-base py-3 text-white transition-all duration-200"
              style={ORANGE_BG_STYLE}
              data-testid="lead-submit-btn"
            >
              {submitting ? 'SENDING...' : 'SEND ME THE RESULTS'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
